import { CurrencyPipe } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { connectState } from '@examdojo/angular/util';
import { CurrencyService } from '@examdojo/billing';
import { ButtonComponent } from '@examdojo/core/button';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { emailValidator } from '@examdojo/core/form';
import { IconComponent } from '@examdojo/core/icon';
import { SingleSelectComponent } from '@examdojo/core/select';
import { ToastService } from '@examdojo/core/toast';
import { PayoutRateHistoryDialogService, PayoutRateQuery } from '@examdojo/payments';
import { TextInputComponent } from '@examdojo/ui/input';
import { UserQuery } from '@examdojo/user';
import { distinctUntilChanged, EMPTY, first, map, of, shareReplay, switchMap, tap } from 'rxjs';
import { EditPayoutAccountDialogResult, EditPayoutAccountDialogService } from './edit-payout-account-dialog';
import { PayoutAccountFormModel } from './payout-account.model';
import { PayoutAccountQuery } from './payout-account.query';
import { PayoutAccountService } from './payout-account.service';
import { PayoutRequestsComponent } from './payout-requests/payout-requests.component';

@Component({
  selector: 'dojo-validation-payment',
  imports: [
    CurrencyPipe,
    ButtonComponent,
    IconComponent,
    TextInputComponent,
    SingleSelectComponent,
    ReactiveFormsModule,
    MatTooltipModule,
    MatExpansionModule,
    PayoutRequestsComponent,
  ],
  templateUrl: './payment.component.html',
  host: { class: 'block w-full h-full' },
  styles: [
    `
      ::ng-deep .payout-account-panel {
        @apply rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800;
        border-radius: 0.75rem !important;

        &.mat-expanded {
          margin: 0;
        }

        .mat-expansion-panel-header {
          @apply px-6 py-4;
          height: auto;
          border-radius: 0.75rem 0.75rem 0 0 !important;

          &:hover {
            @apply bg-gray-50 dark:bg-gray-700;
          }
        }

        .mat-expansion-panel-body {
          @apply px-6 pb-4;
        }

        .mat-expansion-indicator {
          @apply text-gray-600 dark:text-gray-400;
        }

        &.mat-expanded .mat-expansion-panel-header {
          @apply border-b border-gray-100 dark:border-gray-700;
        }
      }

      ::ng-deep .mat-accordion {
        .mat-expansion-panel:not(:first-child) {
          margin-top: 0 !important;
        }
      }
    `,
  ],
})
export class PaymentComponent {
  constructor(
    private readonly payoutAccountService: PayoutAccountService,
    private readonly payoutAccountQuery: PayoutAccountQuery,
    private readonly payoutRateQuery: PayoutRateQuery,
    private readonly userQuery: UserQuery,
    private readonly errorHandlerService: ErrorHandlerService,
    private readonly toastService: ToastService,
    private readonly editPayoutAccountDialogService: EditPayoutAccountDialogService,
    private readonly payoutRateHistoryDialogService: PayoutRateHistoryDialogService,
    private readonly fb: FormBuilder,
    private readonly currencyService: CurrencyService,
  ) {
    // Pre-fill email when user data is available
    this.userQuery.active$
      .pipe(
        first((user) => !!user?.email), // Wait for a user with an email
        map((user) => user?.email || ''),
        tap((email) => {
          if (email && !this.payoutForm.get('email')?.value) {
            this.payoutForm.patchValue({ email });
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.payoutAccountService.loadPayoutAccount().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    // Data loading is handled in ngOnInit
  }

  private readonly destroyRef = inject(DestroyRef);

  readonly payoutForm = this.fb.group({
    email: new FormControl('', {
      validators: [Validators.required, emailValidator],
      nonNullable: true,
    }),
    name: new FormControl('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
    currency: new FormControl('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
  });

  readonly currencies$ = this.currencyService.getCurrencies();

  readonly hourlyRate$ = this.userQuery.active$.pipe(
    distinctUntilChanged((a, b) => a?.id === b?.id),
    switchMap((user) => (user ? this.payoutRateQuery.selectUserRate(user.id) : of(null))),
    map((rate) => rate?.rate ?? null),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly state = connectState({
    payoutAccount: this.payoutAccountQuery.selectPayoutAccount(),
    hasPayoutAccount: this.payoutAccountQuery.selectHasPayoutAccount(),
    error: this.payoutAccountQuery.selectError(),
    isLoading: this.payoutAccountQuery.selectLoading(),
    hourlyRate: this.hourlyRate$,
    currencies: this.currencies$,
  });

  get emailControl() {
    return this.payoutForm.controls.email;
  }

  get nameControl() {
    return this.payoutForm.controls.name;
  }

  get currencyControl() {
    return this.payoutForm.controls.currency;
  }

  setupPayoutAccount(): void {
    if (this.payoutForm.valid) {
      const formValue = this.payoutForm.getRawValue();
      const formData: PayoutAccountFormModel = {
        recipientEmail: formValue.email,
        recipientName: formValue.name,
        recipientCurrency: formValue.currency,
      };

      this.payoutAccountService
        .createPayoutAccount(formData)
        .pipe(
          tap(() => {
            this.toastService.success('Payout account created successfully! Check your email for next steps.');
          }),
          this.errorHandlerService.catchHttpErrors(() => EMPTY),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  editPayoutAccount(): void {
    const currentAccount = this.state.payoutAccount;
    if (!currentAccount) {
      return;
    }

    this.editPayoutAccountDialogService
      .openDialog({
        input: {
          currentEmail: currentAccount.recipient_email,
          currentName: currentAccount.recipient_name,
          currentCurrency: currentAccount.recipient_currency,
        },
      })
      .then((result) => {
        if (result) {
          this.handlePayoutAccountUpdate(result);
        }
      });
  }

  handlePayoutAccountUpdate(result: EditPayoutAccountDialogResult): void {
    this.userQuery.active$
      .pipe(
        first((user) => !!user),
        switchMap((user) => {
          const formData: Partial<PayoutAccountFormModel> = {
            recipientEmail: result.newEmail,
            recipientName: result.newName,
            recipientCurrency: result.newCurrency,
          };
          return this.payoutAccountService.updatePayoutAccount(user.id, formData);
        }),
        tap(() => {
          this.toastService.success('Payout account updated successfully!');
        }),
        this.errorHandlerService.catchHttpErrors(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  viewRateHistory(): void {
    const user = this.userQuery.getActiveUIEntity();
    if (user?.id) {
      this.payoutRateHistoryDialogService.openDialog({
        userId: user.id,
        userName: user.first_name ? `${user.first_name} ${user.last_name}` : undefined,
      });
    }
  }
}
